import { useState, useContext, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Input,
  InputGroupText,
  InputGroup,
  Badge,
} from 'reactstrap';
import { QuestionDetailDialog } from './QuestionDetailDialog';
import { SurveyContext, SurveyContextLoadedProps } from '../../survey-container/SurveyContainer';
import { getSurveyGroupsSelector } from '../../../selectors/survey';
import { Loader } from '../../../atoms/loader';
import { ColumnCode } from '../../survey-question-list/partials/ColumnCode';
import { Divider } from '../../../molecules/tracking-list';
import { ColumnTitle } from '../../../molecules/survey-question-list';
import {
  ExpandingQuestionIds,
  ExpandingSurveyQuestionList,
} from '@features/expanding-survey-question-list/expanding-survey-question-list';
import { useGetBatchResultsQuery, BatchResult, DocumentMetadata } from '../../../api/ai-supervisor';
import { DashboardSection } from '../../../molecules/dashboard';
import { QuestionState } from '../hooks/useBatchSSE';
import { Answer } from '../types';
import { ExpansionContent } from './partials/ExpansionContent';
import { ColumnApproved } from './partials/ColumnApproved';
import { ColumnStatus } from './partials/ColumnStatus';
import { GroupColumnProgress } from './partials/GroupColumnProgress';
import { GroupColumnName } from './partials/GroupColumnName';
import { ColumnStatusIcon } from './partials/ColumnStatusIcon';
import { ColumnExpandIndicator } from './partials/ColumnExpandIndicator';

interface QuestionsListProps {
  batchId: string;
  questionStates: Map<string, QuestionState>;
  documentMetadata?: Record<string, DocumentMetadata>;
}

export const QuestionsList = ({ batchId, questionStates, documentMetadata: sseDocumentMetadata }: QuestionsListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState<Answer | null>(null);
  const [questionIds, setQuestionIds] = useState<ExpandingQuestionIds>({
    openedIds: new Set(),
  });
  const [approvedIds, setApprovedIds] = useState<Set<string>>(new Set());
  const [confidenceFilter, setConfidenceFilter] = useState<('high' | 'medium' | 'low' | 'no-confidence')[]>(['high', 'medium', 'low', 'no-confidence']);
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'processing' | 'pending' | 'failed'>('all');

  const context = useContext(SurveyContext) as SurveyContextLoadedProps;
  const surveyGroups = useSelector(getSurveyGroupsSelector);
  const { surveyData, isLoaded } = context;

  const toggleConfidenceFilter = (filter: 'high' | 'medium' | 'low' | 'no-confidence') => {
    setConfidenceFilter(cur => cur.includes(filter) ? cur.filter((f) => f !== filter) : [...cur, filter]);
  };

  // Fetch batch results
  const { data: batchResults, isLoading: isLoadingResults, error: resultsError } = useGetBatchResultsQuery(
    {
      initiativeId: surveyData.initiativeId,
      surveyId: surveyData._id,
      batchId,
    },
    { skip: !isLoaded || !batchId }
  );

  // Merge document metadata from SSE and batch results (SSE takes precedence as it's real-time)
  const documentMetadata = useMemo(() => {
    const batchDocumentMetadata = batchResults?.job?.metadata?.documentMetadata;
    return sseDocumentMetadata || batchDocumentMetadata;
  }, [sseDocumentMetadata, batchResults]);

  // Create a map of utrvId to batch result for quick lookup
  const resultsMap = useMemo(() => {
    if (!batchResults?.job?.results) return new Map<string, BatchResult>();
    return new Map(
      batchResults.job.results.map((result) => {
        const utrvId = result.utrvId;
        return [utrvId, result];
      })
    );
  }, [batchResults]);

  // Create a map of utrvId to survey question for quick lookup
  const questionsMap = useMemo(() => {
    const map = new Map<string, { questionName: string; questionValueLabel: string; category: string }>();
    surveyGroups.forEach((group) => {
      const category = group.groupName || 'Uncategorized';
      group.list.forEach((question) => {
        const utrvId = question.utrv?._id;
        if (utrvId) {
          map.set(utrvId, {
            questionName: question.universalTracker.getName(),
            questionValueLabel: question.universalTracker.getValueLabel(),
            category,
          });
        }
      });
    });
    return map;
  }, [surveyGroups]);

  // Helper function to get question data from SSE and batch results
  const getAutoAnswer = useCallback((utrvId?: string): Answer | undefined => {
    if (!utrvId) return undefined;

    // Get question metadata from survey
    const questionMeta = questionsMap.get(utrvId);
    const category = questionMeta?.category || 'Uncategorized';

    // Get real-time state from SSE
    const liveState = questionStates.get(utrvId);

    // Get final result from batch results
    const result = resultsMap.get(utrvId);

    // If we have real-time state but no final result, show in-progress state
    if (liveState && !result) {
      return {
        id: utrvId,
        questionName: questionMeta?.questionName || '',
        questionValueLabel: questionMeta?.questionValueLabel || '',
        category,
        status: liveState.status,
        stage: liveState.stage,
        confidence: liveState.result?.confidenceScore ? Math.round(100*liveState.result.confidenceScore) : undefined,
        answer: liveState.result?.answer?.value || undefined,
        approved: false,
        citations: liveState.result?.citations || [],
        citationsCount: liveState.result?.citations?.length || 0,
        retrievedChunks: liveState.result?.retrievedChunks,
        error: liveState.error,
      };
    }
    if (!result) return undefined;

    // Merge real-time state with final result (real-time takes precedence for status)
    return {
      id: result.utrvId,
      questionName: questionMeta?.questionName || '',
      questionValueLabel: questionMeta?.questionValueLabel || '',
      category,
      status: liveState?.status || (result.status === 'completed' ? 'completed' : 'pending'),
      stage: liveState?.stage,
      confidence: Math.round(100*result.confidenceScore),
      answer: result.data.answer?.value,
      approved: approvedIds.has(result.utrvId),
      citations: result.data.citations || [],
      citationsCount: result.data.citations?.length || 0,
      retrievedChunks: result.data.retrievedChunks,
      error: liveState?.error,
    };
  }, [questionStates, resultsMap, questionsMap, approvedIds]);

  // Helper function to check if a question matches the confidence filter
  const matchesConfidenceFilter = useCallback((confidence: number | undefined): boolean => {
    if (confidence === undefined) return confidenceFilter.includes('no-confidence');
    const confidenceLevel = confidence >= 90 ? 'high' : confidence >= 70 ? 'medium' : confidence > 0 ? 'low' : 'no-confidence';
    return confidenceFilter.includes(confidenceLevel);
  }, [confidenceFilter]);

  const matchesStatusFilter = useCallback((status?: string): boolean => {
    if (statusFilter === 'all') return true;
    if (status === undefined) return false;
    return status === statusFilter;
  }, [statusFilter]);

  // Calculate confidence level counts
  const confidenceCounts = useMemo(() => {
    const counts = { all: 0, high: 0, medium: 0, low: 0, noConfidence: 0 };

    surveyGroups.forEach((group) => {
      group.list.forEach((question) => {
        const autoAnswerQuestion = getAutoAnswer(question.utrv?._id);

        // Only count completed questions with confidence scores
        const confidence = autoAnswerQuestion?.confidence ?? 0;
        if (autoAnswerQuestion?.status === 'completed') {
          counts.all++;
          if (confidence >= 90) {
            counts.high++;
          } else if (confidence >= 70) {
            counts.medium++;
          } else if (confidence > 0) {
            counts.low++;
          } else {
            counts.noConfidence++;
          }
        }
      });
    });

    return counts;
  }, [surveyGroups, getAutoAnswer]);

  const disabledUTRs = useMemo(() => {
    const hasFilters = confidenceFilter.length < 4 || statusFilter !== 'all';
    if (!hasFilters) {
      return [];
    }
    return surveyGroups.flatMap((group) => group.list.filter((question) => {
      const autoAnswerQuestion = getAutoAnswer(question.utrv?._id);
      const statusFilterMatch = matchesStatusFilter(autoAnswerQuestion?.status);
      const confidenceFilterMatch = matchesConfidenceFilter(autoAnswerQuestion?.confidence);
      return !statusFilterMatch || !confidenceFilterMatch;
    }).map((question) => question.universalTracker.getId()));
  }, [surveyGroups, getAutoAnswer, confidenceFilter, statusFilter, matchesConfidenceFilter, matchesStatusFilter]);

  if (!isLoaded || isLoadingResults) {
    return <Loader />;
  }

  if (resultsError) {
    return (
      <DashboardSection>
        <div className='alert alert-danger'>
          <i className='fal fa-exclamation-triangle me-2' />
          Failed to load batch results. Please refresh the page to try again.
        </div>
      </DashboardSection>
    );
  }

  const handleApprove = (itemId: string) => {
    setApprovedIds((prev) => new Set(prev).add(itemId));
  };

  return (
    <DashboardSection className='d-flex flex-column'>
      {/* Fixed Header Section - Always visible */}
      <div className='pb-3'>
        <div className='d-flex align-items-center justify-content-between mb-3'>
          <h4>Questions</h4>
          <div className='d-flex align-items-center gap-2 ms-auto'>
            <UncontrolledDropdown>
              <DropdownToggle caret color='transparent'>
                {statusFilter === 'all' && 'All Statuses'}
                {statusFilter === 'completed' && (<><i className='fal fa-check-circle text-success me-1' />Completed</>)}
                {statusFilter === 'failed' && (<><i className='fal fa-exclamation-circle text-danger me-1' />Failed</>)}
                {statusFilter === 'processing' && (<><i className='fal fa-spinner fa-spin text-primary me-1' />Processing</>)}
                {statusFilter === 'pending' && (<><i className='fal fa-clock text-warning me-1' />Pending</>)}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => setStatusFilter('all')} active={statusFilter === 'all'}>
                  All Statuses
                </DropdownItem>
                <DropdownItem onClick={() => setStatusFilter('completed')} active={statusFilter === 'completed'}>
                  <span className='text-success'><i className='fal fa-check-circle me-2' />Completed</span>
                </DropdownItem>
                <DropdownItem onClick={() => setStatusFilter('processing')} active={statusFilter === 'processing'}>
                  <span className='text-primary'><i className='fal fa-spinner fa-spin me-2' />Processing</span>
                </DropdownItem>
                <DropdownItem onClick={() => setStatusFilter('pending')} active={statusFilter === 'pending'}>
                  <span className='text-warning'><i className='fal fa-clock me-2' />Pending</span>
                </DropdownItem>
                <DropdownItem onClick={() => setStatusFilter('failed')} active={statusFilter === 'failed'}>
                  <span className='text-danger'><i className='fal fa-exclamation-circle me-2' />Failed</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <div className='d-flex align-items-center gap-2'>
              <Button
                size='sm'
                color={'success'}
                outline={!confidenceFilter.includes('high')}
                onClick={() => toggleConfidenceFilter('high')}
                className='d-flex align-items-center gap-2'
              >
                <span className={confidenceFilter.includes('high') ? 'text-white' : 'text-success'}>
                  High (≥90%)
                </span>
                <Badge color={'success'}>
                  {confidenceCounts.high}
                </Badge>
              </Button>
              <Button
                size='sm'
                color={'warning'}
                outline={!confidenceFilter.includes('medium')}
                onClick={() => toggleConfidenceFilter('medium')}
                className='d-flex align-items-center gap-2'
              >
                <span className={confidenceFilter.includes('medium') ? 'text-white' : 'text-warning'}>
                  Medium (70-89%)
                </span>
                <Badge color={'warning'}>
                  {confidenceCounts.medium}
                </Badge>
              </Button>
              <Button
                size='sm'
                color={'danger'}
                outline={!confidenceFilter.includes('low')}
                onClick={() => toggleConfidenceFilter('low')}
                className='d-flex align-items-center gap-2'
              >
                <span className={confidenceFilter.includes('low') ? 'text-white' : 'text-danger'}>
                  Low (&lt;70%)
                </span>
                <Badge color={'danger'}>
                  {confidenceCounts.low}
                </Badge>
              </Button>
              <Button
                size='sm'
                color={'dark'}
                outline={!confidenceFilter.includes('no-confidence')}
                onClick={() => toggleConfidenceFilter('no-confidence')}
                className='d-flex align-items-center gap-2'
              >
                <span className={confidenceFilter.includes('no-confidence') ? '' : 'text-muted'}>
                  None (0%)
                </span>
                <Badge color={'secondary'}>
                  {confidenceCounts.noConfidence}
                </Badge>
              </Button>
            </div>
          </div>
        </div>

        <InputGroup>
          <InputGroupText>
            <i className='fal fa-search' />
          </InputGroupText>
          <Input
            placeholder='Search across all questions...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </div>

      {/* Scrollable Content Section - Questions list only */}
      <div className='flex-1 overflow-auto' style={{ minHeight: 0 }}>
        <ExpandingSurveyQuestionList
          handleReload={() => Promise.resolve()} // @TODO ??
          survey={surveyData}
          surveyId={surveyData._id}
          initiativeId={surveyData.initiativeId}
          surveyGroups={surveyGroups}
          disabledUTRs={disabledUTRs}
          noRowsMessage={'No questions available'}
          allowBulkActions={false}
          handleExpandRow={setQuestionIds}
          columns={[
            (props) => <ColumnStatusIcon {...props} answer={getAutoAnswer(props.question.utrv?._id)} />,
            ColumnCode,
            Divider,
            ColumnTitle,
            (props) => <ColumnApproved {...props} answer={getAutoAnswer(props.question.utrv?._id)} />,
            (props) => {
              const answer = getAutoAnswer(props.question.utrv?._id);
              if (!answer || answer.status !== 'completed') {
                return null;
              }
              const textColor = !answer.confidence ? 'text-ThemeNeutralsMedium' : answer.confidence < 50 ? 'text-ThemeDangerMedium' : answer.confidence < 90 ? 'text-ThemeWarningMedium' : 'text-ThemeSuccessMedium';
              return (
                <ColumnStatus
                  {...props}
                  icon='fa-chart-mixed'
                  iconColor={textColor}
                  textColor={textColor}
                  value={`${answer.confidence ?? '-'}%`}
                />
              );
            },
            (props) => {
              const answer = getAutoAnswer(props.question.utrv?._id);
              if (!answer || answer.status !== 'completed') {
                return null;
              }
              const iconColor = !answer.citations ? 'text-ThemeNeutralsMedium' : 'text-ThemeSuccessMedium';
              const textColor = !answer.citations ? 'text-ThemeNeutralsMedium' : 'text-ThemeTextDark';
              return (
                <ColumnStatus
                  {...props}
                  icon='fa-quote-right'
                  iconColor={iconColor}
                  textColor={textColor}
                  value={answer.citationsCount}
                />
              );
            },
            (props) => <ColumnExpandIndicator {...props} expandingId={questionIds.expandingId} />,
            Divider,
          ]}
          groupColumns={[
            ({ questionGroup }) => <GroupColumnName questionGroup={questionGroup} key={'group-column-name'} />,
            ({ questionGroup }) => <GroupColumnProgress
              questionGroup={questionGroup}
              getLiveState={(utrvId) => questionStates.get(utrvId)}
              getResult={(utrvId) => resultsMap.get(utrvId)}
              key={'group-column-progress'}
            />,
          ]}
          expansionRowContent={(question) => {
            const autoAnswerQuestion = getAutoAnswer(question.utrv?._id);
            return <ExpansionContent
              question={question}
              answer={autoAnswerQuestion}
              documentMetadata={documentMetadata}
              handleApprove={handleApprove}
              setSelectedAnswer={setSelectedAnswer}
            />;
          }}
        />
      </div>

      <QuestionDetailDialog answer={selectedAnswer} onClose={() => setSelectedAnswer(null)} documentMetadata={documentMetadata} />
    </DashboardSection>
  );
}
